/**
 * Cross-origin API client. Mirrors docreview's pattern: SPA acquires an idToken
 * via MSAL.js and passes it as `Authorization: Bearer <jwt>` to the Function App.
 * The Function App's Easy Auth v2 validates the token and sets the
 * `x-ms-client-principal-*` headers that route handlers read.
 *
 * Components must gate API calls on `tokenReady` from useAuth().
 */
import { apiConfig } from '../authConfig';

let _getToken: (() => Promise<string | null>) | null = null;
let _activeAccountKey: string = 'anon';

export function registerTokenGetter(getter: () => Promise<string | null>): void {
  _getToken = getter;
}

export function setActiveAccountKey(key: string | null): void {
  const next = key ?? 'anon';
  if (next !== _activeAccountKey) {
    cache.clear();
  }
  _activeAccountKey = next;
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipCache?: boolean;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

interface CacheEntry {
  expiresAt: number;
  json: unknown;
}

const GET_CACHE_TTL_MS = 5_000;
const cache = new Map<string, CacheEntry>();

function cacheKey(url: string, init: RequestInit): string {
  return `${_activeAccountKey}::${init.method ?? 'GET'} ${url}`;
}

async function authedFetch(url: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (_getToken) {
    const token = await _getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(url, { ...init, headers });
}

export async function apiCall<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
    (options.method ?? 'GET').toUpperCase(),
  );

  let body: BodyInit | undefined;
  if (options.body !== undefined && options.body !== null) {
    body = options.body instanceof FormData ? options.body : JSON.stringify(options.body);
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers: options.headers,
    body,
    credentials: 'omit',
    mode: 'cors',
  };

  const fullUrl = path.startsWith('http')
    ? path
    : `${apiConfig.apiEndpoint}${path.startsWith('/') ? path : '/' + path}`;
  const key = cacheKey(fullUrl, init);

  if (!isMutation && !options.skipCache) {
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.json as T;
    }
  }

  const response = await authedFetch(fullUrl, init);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let message: string;
    try {
      const parsed = JSON.parse(text);
      message = typeof parsed?.error === 'string' ? parsed.error : `HTTP ${response.status}`;
    } catch {
      message = text || `HTTP ${response.status}`;
    }
    throw new ApiError(response.status, message);
  }

  const json = await response.json().catch(() => null);
  if (
    json &&
    typeof json === 'object' &&
    'success' in json &&
    (json as { success: boolean }).success === false
  ) {
    throw new ApiError(response.status, (json as { error?: string }).error ?? 'API error');
  }
  const data =
    json && typeof json === 'object' && 'data' in (json as object)
      ? (json as { data: unknown }).data
      : json;

  if (!isMutation) {
    cache.set(key, { expiresAt: Date.now() + GET_CACHE_TTL_MS, json: data });
  } else {
    cache.clear();
  }

  return data as T;
}

export function invalidateApiCache(): void {
  cache.clear();
}

export async function apiCallRaw(path: string, options: ApiOptions = {}): Promise<Response> {
  let body: BodyInit | undefined;
  if (options.body !== undefined && options.body !== null) {
    body = options.body instanceof FormData ? options.body : JSON.stringify(options.body);
  }
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers: options.headers,
    body,
    credentials: 'omit',
    mode: 'cors',
  };
  const fullUrl = path.startsWith('http')
    ? path
    : `${apiConfig.apiEndpoint}${path.startsWith('/') ? path : '/' + path}`;
  const response = await authedFetch(fullUrl, init);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, text || `HTTP ${response.status}`);
  }
  return response;
}
